"use client";

import { useState, useEffect } from "react";
import { Heart, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { useLanguage } from "../../hooks/useLanguage";
import config from "../../config/config";

export default function OurInspirationSection() {
  const [inspirationData, setInspirationData] = useState(null);
  const [sectionTitle, setSectionTitle] = useState({ en: "OUR INSPIRATION", hi: "हमारी प्रेरणा" });
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchInspirationSettings = async () => {
      try {
        const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/inspiration`);
        const data = await response.json();
        if (data.success && data.data.item) {
          setInspirationData(data.data.item);
        }
        if (data.success && data.data.sectionTitle) {
          setSectionTitle(data.data.sectionTitle);
        }
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    };

    fetchInspirationSettings();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };
  
  if (loading) {
    return null;
  }
  
  if (!inspirationData) {
    return null;
  }

  // Check if there's any meaningful content to display
  const hasContent = inspirationData.desktopImage ? true : 
                     (getLabel(inspirationData.title) ? true : 
                     (getLabel(inspirationData.excerpt) ? true : false));

  if (!hasContent) {
    return null;
  }

  return (
    <div className="relative flex flex-row md:bg-gray-50 md:rounded-lg md:overflow-hidden">
      {/* Content - Left side for both mobile and desktop */}
      <div className="flex-1 flex items-start md:items-center p-2 sm:p-3 md:p-4 lg:p-6">
        <div className="space-y-1 sm:space-y-2 md:space-y-3 lg:space-y-4 w-full">
          {getLabel(sectionTitle) && (
          <div>
            {/* Small device - normal text */}
            <h4 className="md:hidden text-xs font-medium text-primary tracking-wider uppercase">
              {getLabel(sectionTitle)}
            </h4>
            {/* Medium+ devices - badge */}
            <div className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              <Heart className="w-4 h-4 text-primary" />
              <span className="font-bold text-sm lg:text-sm text-primary tracking-wider">
                {getLabel(sectionTitle)}
              </span>
            </div>
          </div>
          )}

          {getLabel(inspirationData.title) && (
          <h3 className="text-sm sm:text-lg md:text-xl lg:text-xl xl:text-2xl font-bold text-gray-900 leading-tight">
            {getLabel(inspirationData.title)}
          </h3>
          )}

          {getLabel(inspirationData.excerpt) && (
          <p className="text-xs sm:text-sm md:text-base lg:text-base text-gray-600 leading-relaxed line-clamp-3 sm:line-clamp-none">
            {getLabel(inspirationData.excerpt)}
          </p>
          )}

          {inspirationData.button?.link && getLabel(inspirationData.button.text) && (
            <Button
              size="sm"
              className="px-2 sm:px-4 md:px-6 py-1.5 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-semibold rounded-lg transition-all duration-300 hover:scale-105 group"
              style={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
                border: 'none'
              }}
              onClick={() => {
                const target = inspirationData.button.target ? inspirationData.button.target : '_self';
                const link = inspirationData.button.link;
                if (target === "_blank") {
                  window.open(link, "_blank");
                } else {
                  window.location.href = link;
                }
              }}
            >
              {getLabel(inspirationData.button.text)}
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          )}
        </div>
      </div>

      {/* Image - Right side for both mobile and desktop */}
      {inspirationData.desktopImage && (
        <div className="w-24 sm:w-32 md:flex-1 overflow-hidden rounded-lg">
          {/* Desktop Image */}
          <img
            src={inspirationData.desktopImage}
            alt={getLabel(inspirationData.title)}
            className="hidden md:block w-full h-full object-cover hover:scale-105 transition-transform duration-700"
          />
          {/* Mobile Image - square format */}
          <img
            src={inspirationData.mobileImage ? inspirationData.mobileImage : inspirationData.desktopImage}
            alt={getLabel(inspirationData.title)}
            className="block md:hidden w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-700"
          />
        </div>
      )}
    </div>
  );
}
